/**
 * Тема и плотность.
 *
 * Значения живут на корневом элементе как data-атрибуты: токены в tokens.css
 * переопределяются по ним, поэтому переключение не требует ни пересборки,
 * ни `dark:`-вариантов у каждого класса.
 *
 * localStorage — это кэш для мгновенного применения при загрузке; источником
 * истины остаётся профиль пользователя на сервере.
 */

export type Theme = "light" | "dark" | "system";
export type Density = "compact" | "normal" | "comfortable";

/**
 * Оформление — набор значений токенов, а не отдельная тема.
 *
 * «Тихая бумага» — значения по умолчанию. «Рабочий стол» уводит обвязку
 * (шапку и левую панель) в тёмное и добавляет строкам чередование и засечку
 * статуса. Оба оформления работают и в светлой теме, и в тёмной: тема
 * отвечает за освещение, оформление — за расстановку акцентов.
 */
export type Skin = "paper" | "desk";

const THEME_KEY = "pt-theme";
const DENSITY_KEY = "pt-density";
const SKIN_KEY = "pt-skin";

/** Класс на время смены темы глушит переходы: иначе интерфейс «переливается». */
const SWITCHING_CLASS = "pt-theme-switching";

export const THEMES: readonly Theme[] = ["light", "dark", "system"];
export const DENSITIES: readonly Density[] = ["compact", "normal", "comfortable"];

export const SKINS = [
  { value: "paper", label: "Тихая бумага" },
  { value: "desk", label: "Рабочий стол" },
] as const satisfies readonly { value: Skin; label: string }[];

function isTheme(value: string | null): value is Theme {
  return value !== null && (THEMES as readonly string[]).includes(value);
}

function isDensity(value: string | null): value is Density {
  return value !== null && (DENSITIES as readonly string[]).includes(value);
}

function isSkin(value: string | null): value is Skin {
  return value !== null && SKINS.some((skin) => skin.value === value);
}

export function getTheme(): Theme {
  const stored = localStorage.getItem(THEME_KEY);
  return isTheme(stored) ? stored : "system";
}

export function getDensity(): Density {
  const stored = localStorage.getItem(DENSITY_KEY);
  return isDensity(stored) ? stored : "normal";
}

export function setTheme(next: Theme): void {
  const el = document.documentElement;
  el.classList.add(SWITCHING_CLASS);

  if (next === "system") {
    delete el.dataset["theme"];
  } else {
    el.dataset["theme"] = next;
  }
  localStorage.setItem(THEME_KEY, next);

  // Два кадра: первый применяет новые значения, второй возвращает переходы
  requestAnimationFrame(() => requestAnimationFrame(() => el.classList.remove(SWITCHING_CLASS)));
}

export function setDensity(next: Density): void {
  document.documentElement.dataset["density"] = next;
  localStorage.setItem(DENSITY_KEY, next);
}

export function getSkin(): Skin {
  const stored = localStorage.getItem(SKIN_KEY);
  return isSkin(stored) ? stored : "paper";
}

export function setSkin(next: Skin): void {
  const el = document.documentElement;
  // Переходы глушатся тем же приёмом, что при смене темы: иначе половина
  // экрана перекрашивается заметно позже другой
  el.classList.add(SWITCHING_CLASS);
  el.dataset["skin"] = next;
  localStorage.setItem(SKIN_KEY, next);
  requestAnimationFrame(() => requestAnimationFrame(() => el.classList.remove(SWITCHING_CLASS)));
}

/** Что реально видит пользователь при теме «системная». */
export function resolveTheme(theme: Theme): Exclude<Theme, "system"> {
  if (theme !== "system") {
    return theme;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/* ── Личные цвета ───────────────────────────────────────────────────────── */

/**
 * Что человек выбирает себе сам.
 *
 * Список закрытый: цвет попадает в стиль страницы, и «любой токен на выбор»
 * означал бы «любое правило оформления на выбор». Остальные цвета выводятся
 * из выбранных, чтобы наведение и нажатие не пришлось подбирать отдельно.
 */
export const COLOR_SLOTS = [
  { key: "canvas", label: "Полотно", token: "--pt-bg-canvas" },
  { key: "rail", label: "Боковая панель", token: "--pt-bg-rail" },
  { key: "surface", label: "Карточка и шапка", token: "--pt-bg-surface" },
  { key: "accent", label: "Действие", token: "--pt-accent" },
  { key: "link", label: "Ссылка", token: "--pt-accent-text" },
  { key: "hover", label: "Строка под курсором", token: "--pt-bg-hover" },
] as const;

export type ColorSlot = (typeof COLOR_SLOTS)[number]["key"];
export type Colors = Partial<Record<ColorSlot, string>>;

const COLORS_KEY = "pt-colors";
const HEX = /^#[0-9a-fA-F]{6}$/;

export function getColors(): Colors {
  try {
    const raw = localStorage.getItem(COLORS_KEY);
    if (raw === null) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    return sanitize(parsed);
  } catch {
    return {};
  }
}

/** Отсеять всё, что не является известным цветом: значение идёт в стиль. */
export function sanitize(value: unknown): Colors {
  if (typeof value !== "object" || value === null) {
    return {};
  }
  const result: Colors = {};
  for (const slot of COLOR_SLOTS) {
    const color: unknown = (value as Record<string, unknown>)[slot.key];
    if (typeof color === "string" && HEX.test(color)) {
      result[slot.key] = color;
    }
  }
  return result;
}

/**
 * Светлота цвета по восприятию, 0…1.
 *
 * Нужна ровно для одного решения: писать на этом фоне тёмным или светлым.
 * Веса каналов — из формулы яркости WCAG, без гамма-коррекции: для выбора
 * между «чёрным» и «белым» её точности хватает, а считать это приходится
 * на каждое движение ползунка.
 */
function lightness(hex: string): number {
  const value = Number.parseInt(hex.slice(1), 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  return (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
}

/** Порог, за которым фон считается светлым и надпись на нём должна темнеть. */
const LIGHT_ENOUGH = 0.62;

/** Три ступени надписи на выбранном фоне: основная, вторая, третья. */
function inkOn(background: string): [string, string, string] {
  const dark = lightness(background) > LIGHT_ENOUGH;
  const ink = dark ? "oklch(0.235 0.012 80)" : "oklch(0.965 0.004 250)";
  return [
    ink,
    `color-mix(in oklab, ${ink} 76%, ${background})`,
    `color-mix(in oklab, ${ink} 58%, ${background})`,
  ];
}

/** Какие области обвязки красит слот и какие переменные надписи ему принадлежат. */
const CHROME: Partial<Record<ColorSlot, { surface: string; ink: string }>> = {
  rail: { surface: "--pt-chrome-rail", ink: "--pt-ink-rail" },
  surface: { surface: "--pt-chrome-bg", ink: "--pt-ink-bar" },
};

/**
 * Надпись на содержимом считается по цвету карточки.
 *
 * Карточка несёт почти весь текст в системе, поэтому тёмный цвет карточки
 * обязан увести надпись в светлое — иначе выбор цвета делает содержимое
 * нечитаемым. Полотно за пределами карточек текста почти не несёт, и цвет
 * надписи берётся не по нему.
 */
const CONTENT_INK = ["--pt-text-primary", "--pt-text-secondary", "--pt-text-tertiary"] as const;

/**
 * Применить цвета к странице.
 *
 * Производные (наведение и нажатие у действия) считаются из выбранного цвета
 * смешиванием: подбирать вручную шесть оттенков — работа не для человека,
 * который просто хочет синюю кнопку.
 *
 * Панель и шапка задают ещё и цвет надписи на себе. Без этого выбор тёмного
 * цвета панели оставлял на ней тёмную надпись, а в оформлении «Рабочий стол»
 * выбор вообще не доходил до обвязки: её цвет задаёт оформление.
 */
export function applyColors(colors: Colors): void {
  const el = document.documentElement;
  for (const slot of COLOR_SLOTS) {
    const color = colors[slot.key];
    const chrome = CHROME[slot.key];
    if (color === undefined) {
      el.style.removeProperty(slot.token);
      if (chrome !== undefined) {
        el.style.removeProperty(chrome.surface);
        for (const suffix of ["", "-2", "-3"]) {
          el.style.removeProperty(chrome.ink + suffix);
        }
      }
    } else {
      el.style.setProperty(slot.token, color);
      if (chrome !== undefined) {
        el.style.setProperty(chrome.surface, color);
        const shades = inkOn(color);
        el.style.setProperty(chrome.ink, shades[0]);
        el.style.setProperty(`${chrome.ink}-2`, shades[1]);
        el.style.setProperty(`${chrome.ink}-3`, shades[2]);
      }
    }
    if (slot.key === "surface") {
      const shades = color === undefined ? null : inkOn(color);
      CONTENT_INK.forEach((token, level) => {
        if (shades === null) {
          el.style.removeProperty(token);
        } else {
          el.style.setProperty(token, shades[level] ?? shades[0]);
        }
      });
    }
  }

  const accent = colors.accent;
  for (const [token, mix] of [
    ["--pt-accent-hover", 88],
    ["--pt-accent-active", 76],
  ] as const) {
    if (accent === undefined) {
      el.style.removeProperty(token);
    } else {
      el.style.setProperty(token, `color-mix(in oklab, ${accent} ${String(mix)}%, black)`);
    }
  }
}

export function setColors(next: Colors): void {
  const clean = sanitize(next);
  localStorage.setItem(COLORS_KEY, JSON.stringify(clean));
  applyColors(clean);
}
